import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslateService } from "@ngx-translate/core";
import { MarketsService, RetailersService, MarketResponse, RetailerResponse, UsersService, RoleResponse, RolesService, UserRequest, UserResponse, LanguageResponse, FilterRequestForCombo } from '@src/app/shared/backend/clients';
import { passwordRegexValidator } from '@src/app/shared/validators/password.validator';
import { confirmPasswordValidator } from '@src/app/shared/validators/confirmPassword.validator';
import { spacesValidator } from '@src/app/shared/validators/trimSpaces.validator';
import { forkJoin, firstValueFrom, of } from 'rxjs';
import { debounceTime, switchMap, distinctUntilChanged } from 'rxjs/operators';
import { SessionService } from '@src/app/shared/services/session.service';
import { FilterOperator } from '@app/shared/enums/FilterOperator.enum';
import { latinCharsetRegexValidator } from '@src/app/shared/validators/latinCharacters.validator';
import { emailRegexValidator } from '@src/app/shared/validators/email.validator';
import { Restrictions } from '@src/app/shared/enums/Restrictions.enum';
import { BaseViewComponent } from '@src/app/shared/components/base-list/base-view.component';

@Component({
    selector: 'app-edit-user',
    templateUrl: './edit-user.component.html',
    styleUrl: './edit-user.component.scss',
    host: {
        class: 'app-edit-user'
    },
    standalone: false
})
export class EditUserComponent extends BaseViewComponent implements OnInit {

  isSuperAdmin: boolean = false;
  userId: string = '';
  user: UserResponse = {};

  isMarketsFieldDisabled = false;
  isRetailersFieldDisabled = true;
  filterOperator = FilterOperator;

  role: RoleResponse = {};
  rolesList: any[] = [];
  rolesTotalRecords: number = 0;
  rolesRowsPerPageOption: number = 5;
  selectedRole: any = null;

  store: RetailerResponse = {};
  storesList: any[] = [];
  storesTotalRecords: number = 0;
  storesRowsPerPageOption: number = 5;
  selectedStores: any[] = [];

  market: MarketResponse = {};
  marketsList: any[] = [];
  marketsTotalRecords: number = 0;
  marketsRowsPerPageOption: number = 5;
  selectedMarkets: any[] = [];

  languages: LanguageResponse[] = [];
  language: any;

  public userForm: FormGroup = this.fb.group({
    firstname: ['', [Validators.required, Validators.maxLength(50)]],
    lastname: ['', [Validators.required, Validators.maxLength(50)]],
    romanisedFirstName: ['', [latinCharsetRegexValidator()]],
    romanisedLastName: ['', [latinCharsetRegexValidator()]],
    roles: [[], [Validators.required]],
    roleId: [null],
    username: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(50), spacesValidator()]],
    email: ['', [Validators.required, emailRegexValidator(), Validators.maxLength(50)]],
    stores: [[]],
    storesIds: [null],
    markets: [[]],
    marketsIds: [null],
    allowPasswordChange: ['false'],
    changePassword: [false],
    password: ['', [passwordRegexValidator()]],
    confirmPassword: ['', [confirmPasswordValidator('password')]],
    isActive: [true],
    selectedLanguage: [[]]
  });

  constructor(
    private changeDetector: ChangeDetectorRef,
    private translate: TranslateService,
    private route: ActivatedRoute,
    private router: Router,
    private session: SessionService,
    private fb: FormBuilder,
    private rolesService: RolesService,
    private usersService: UsersService,
    private marketsService: MarketsService,
    private retailersService: RetailersService,
  ) {
    super();
   }

  override async setData(data: any) {

    if (!data) {
      return;
    }

    this.userId = data;
    await this.getLanguages();
    await this.loadAllFormDataAndInit();

    this.userForm.get('roles')?.valueChanges.subscribe((roleIds) => {
      this.handleRoleChange(roleIds);
    });

    this.userForm.get('stores')?.valueChanges.subscribe((storeIds) => {
      this.handleStoresChange(storeIds);
    });

    this.userForm.get('markets')?.valueChanges.subscribe((marketIds) => {
      this.handleMarketsChange(marketIds);
    });

    this.handleUsernameField();
    this.changeDetector.detectChanges();
  }


  ngOnInit() {
    this.init();
  }

  private async init(): Promise<void> {
    this.route.params.subscribe(async params => {
      this.userId = params['userId'];

      if (this.userId) {
        await this.getLanguages();
        await this.loadAllFormDataAndInit();

        this.userForm.get('roles')?.valueChanges.subscribe((roleIds) => {
          this.handleRoleChange(roleIds);
        });

        this.userForm.get('stores')?.valueChanges.subscribe((storeIds) => {
          this.handleStoresChange(storeIds);
        });

        this.userForm.get('markets')?.valueChanges.subscribe((marketIds) => {
          this.handleMarketsChange(marketIds);
        });

        this.handleUsernameField();
        this.changeDetector.detectChanges();
      }
    });
  }

  async loadAllFormDataAndInit(): Promise<void> {
    const user = await firstValueFrom(this.usersService.apiUsersIdGet(this.userId));
    this.user = user;

    // retailers as string IDs
    const assignedRetailers: RetailerResponse[] = Array.isArray(user?.retailers) ? user.retailers : [];
    const assignedRetailerIds: string[] = [];

    for (const retailer of assignedRetailers) {
      if (retailer?.id != null) {
        assignedRetailerIds.push(`${retailer.id}`);
      }
    }

    const retailerFilters: any[] = [];
    if (assignedRetailerIds.length > 0) {
      retailerFilters.push({
        propertyName: 'id',
        operator: this.filterOperator.NOT_IN,
        values: assignedRetailerIds
      });
    }

    // markets as string IDs
    const assignedMarkets: MarketResponse[] = Array.isArray(user?.markets) ? user.markets : [];
    const assignedMarketIds: string[] = [];

    for (const market of assignedMarkets) {
      if (market?.id != null) {
        assignedMarketIds.push(`${market.id}`);
      }
    }

    const marketFilters: any[] = [];
    if (assignedMarketIds.length > 0) {
      marketFilters.push({
        propertyName: 'id',
        operator: this.filterOperator.NOT_IN,
        values: assignedMarketIds
      });
    }

    // load dropdowns data in parallel via forkJoin
    const [rolesPage, unassignedRetailersPage, unassignedMarketsPage] = await firstValueFrom(
      forkJoin([
        this.rolesService.apiRolesFilterComboPost({
          pageNumber: 1,
          pageSize: this.rolesRowsPerPageOption,
          additionalFilters: [{ propertyName: 'isActive', operator: 'eq', values: ['true'] }]
        }),
        this.retailersService.apiRetailersFilterComboPost({
          pageNumber: 1,
          pageSize: this.storesRowsPerPageOption,
          additionalFilters: retailerFilters
        }),
        this.marketsService.apiMarketsFilterComboPost({
          pageNumber: 1,
          pageSize: this.marketsRowsPerPageOption,
          additionalFilters: marketFilters
        }),
      ])
    );

    // assign results
    this.userForm.get('roles')?.valueChanges.subscribe((roleIds) => {
      this.handleRoleChange(roleIds);
    });

    this.userForm.get('stores')?.valueChanges.subscribe((storeIds) => {
      this.handleStoresChange(storeIds);
    });

    this.userForm.get('markets')?.valueChanges.subscribe((marketIds) => {
      this.handleMarketsChange(marketIds);
    });

    const assignedRetailerOptions = assignedRetailers.map((retailer: any) => ({
      id: retailer.id,
      text: `${retailer.name} (${retailer.code})`
    }));

    this.storesList = [...assignedRetailerOptions, ...(unassignedRetailersPage.results ?? [])];
    this.storesTotalRecords = unassignedRetailersPage.totalCount ?? 0;

    const assignedMarketOptions = assignedMarkets.map((market: any) => ({
      id: market.id,
      text: `${market.name} (${market.code})`
    }));

    this.marketsList = [...assignedMarketOptions, ...(unassignedMarketsPage.results ?? [])];
    this.marketsTotalRecords = unassignedMarketsPage.totalCount ?? 0;

    this.rolesList = rolesPage.results ?? [];
    this.rolesTotalRecords = rolesPage.totalCount ?? 0;

    const currentRole = this.user?.role ?? null;

    if (currentRole?.id) {
      const exists = this.rolesList.some((role: any) => String(role.id) === String(currentRole.id));

      if (!exists) {
        this.rolesList = [currentRole, ...this.rolesList];
      }
    }

    this.initForm();
  }

  private findRoleById(roleId: any): any | null {
    if (roleId == null) return null;

    const found = (this.rolesList ?? []).find(role => String(role?.id) === String(roleId));
    return found ?? null;
  }

  private handleRoleChange(roleIds: any): void {
    const selectedRoleId = Array.isArray(roleIds) ? roleIds[0] : null;
    const selectedRole = this.findRoleById(selectedRoleId);

    if(selectedRole.name != null)
    {
      selectedRole.text = selectedRole.name;
    }
    this.selectedRole = selectedRole;

    this.selectedMarkets = [];
    this.selectedStores = [];

    this.userForm.patchValue({
      markets: [],
      marketsIds: [],
      stores: [],
      storesIds: []
    }, { emitEvent: false });

    const rolesControl = this.userForm.get('roles');

    if (!selectedRole) {
      rolesControl?.setValue([], { emitEvent: false });
      rolesControl?.markAsTouched();
      rolesControl?.setErrors({ required: true });
      this.getRoles({ pageIndex: 1, pageSize: this.rolesRowsPerPageOption, search: '' });
      return;
    }

    rolesControl?.markAsDirty();
    rolesControl?.setErrors(null);

    this.updateValidatorsBasedOnRoles(selectedRole);
  }

  private handleStoresChange(storeIds: any): void {
    const ids = Array.isArray(storeIds) ? storeIds : [];
    this.selectedStores = ids;

    this.isMarketsFieldDisabled = false;
    this.userForm.get('marketsIds')?.clearValidators();
    this.userForm.get('marketsIds')?.updateValueAndValidity();

    this.userForm.patchValue({
      storesIds: ids
    }, { emitEvent: false });
  }

  private handleMarketsChange(marketIds: any): void {
    const ids = Array.isArray(marketIds) ? marketIds : [];
    this.selectedMarkets = ids;

    this.isRetailersFieldDisabled = false;
    this.userForm.get('storesIds')?.clearValidators();
    this.userForm.get('storesIds')?.updateValueAndValidity();

    this.userForm.patchValue({
      marketsIds: ids
    }, { emitEvent: false });
  }

  handleUsernameField() {
    this.userForm.get('username')?.valueChanges.subscribe(value => {
      const trimmedValue = value.trim();
      if (value !== trimmedValue) {
        this.userForm.get('username')?.setValue(trimmedValue, { emitEvent: false }); // false to prevent valueChanges loop
      }
    });

    // validate username
    this.userForm.get('username')?.valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      switchMap(async value => {
        if (value) {
          const filters: any[] = [];

          filters.push({
            propertyName: 'username',
            operator: this.filterOperator.EQUALS,
            values: [value]
          });
          const payload : FilterRequestForCombo = {
            pageNumber : 1,
            pageSize : 1,
            additionalFilters : filters
          }

          // usernameCheck Observable
          const usernameCheck = await firstValueFrom(this.usersService.apiUsersFilterComboPost(payload));
          return usernameCheck;
        }

        return of({});
      })
    ).subscribe((usernameCheck: any) => {
      if (usernameCheck?.results.length > 0) {
        this.userForm.get('username')?.setErrors({ usernameTaken: true });
      } else {
        this.userForm.get('username')?.setErrors(null);
      }
    });
  }

  private getLanguages() {
    const languages = this.session.companySettings?.languages ?? [];
    const enabledLanguages = languages.filter(language => language.isEnabled);

    const translation = this.translate.instant('PORTAL_LANGUAGES');
    const transformedLanguages = enabledLanguages.map(lang => {
      const code = lang.code || 'unknown';

      return {
        id: lang.id,
        language: translation[code.toUpperCase()],
        code: code
      };
    });

    this.languages = transformedLanguages as any;
  }

  initForm() {
    // match selected stores and markets using the loaded options
    this.selectedStores = (this.user?.retailers || []).map((store: any) => store.id).filter((id: any) => id != null);
    this.selectedMarkets = (this.user?.markets || []).map((market: any) => market.id).filter((id: any) => id != null);
    this.selectedRole = this.user?.role ?? null;

    // bugfix to prevent ajax custom validation automatic valueChange emission
    this.userForm.get('username')?.setValue(this.user.username, { emitEvent: false });

    this.userForm.patchValue({
      email: this.user.email,
      firstname: this.user.firstName,
      lastname: this.user.lastName,
      romanisedFirstName: this.user.romanisedFirstName,
      romanisedLastName: this.user.romanisedLastName,
      selectedLanguage: this.user?.language?.code ? [this.user.language.code] : [],
      roles: this.selectedRole?.id ? [this.selectedRole.id] : [],
      stores: this.selectedStores,
      storesIds: this.selectedStores,
      markets: this.selectedMarkets,
      marketsIds: this.selectedMarkets,
      isActive: this.user.isActive,
      allowPasswordChange: this.user?.allowedChangePassword ? 'true' : 'false'
    });

    this.updateValidatorsBasedOnRoles(this.selectedRole);
  }

  async getUserId() {
    this.user = await firstValueFrom(this.usersService.apiUsersIdGet(this.userId));

    this.initForm();
  }

  transformRoles(roles: any[]) {
    if (!Array.isArray(roles)) {
      return [];
    }

    roles.map((item) => ({
      id: item.role?.id,
      name: item.role?.text
    }));

    this.userForm.patchValue({
      roles: roles
    });

    return roles;
  }

  getTotalPages(totalRecords: number, pageSize: number): number {
    return Math.max(1, Math.ceil((totalRecords ?? 0) / (pageSize || 1)));
  }

  transformRetailers(retailers: any[]): any[] {
    if (!Array.isArray(retailers)) {
      return [];
    }
    return retailers.map((item) => ({
      id: item.id,
      name: item.text,
    }));
  }

  transformMarkets(markets: any[]): any[] {
    if (!Array.isArray(markets)) {
      return [];
    }
    return markets.map((item) => ({
      id: item.id,
      name: item.text,
    }));
  }

  onLazyLoadRoles(event: any) {
    this.getRoles(event);
  }

  async getRoles(event: any) {
    const filters: any[] = [];
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, event?.pageIndex ?? 1);

    if (search) {
      filters.push({
        propertyName: 'name',
        operator: this.filterOperator.CONTAINS,
        values: [search]
      });
    }

    filters.push({
      propertyName: 'isActive',
      operator: this.filterOperator.EQUALS,
      values: ['true']
    });

    const payload = {
      pageNumber: activePage,
      pageSize: event?.pageSize ?? this.rolesRowsPerPageOption,
      additionalFilters: filters
    };

    const rolesPage = await firstValueFrom(this.rolesService.apiRolesFilterComboPost(payload));

    const fetchedRoles = rolesPage.results ?? [];
    const selectedRoleId = this.user?.role?.id ?? null;

    if (selectedRoleId) {
      const exists = fetchedRoles.some((role: any) => String(role.id) === String(selectedRoleId));

      if (!exists && this.user?.role) {
        this.rolesList = this.removeDuplicatesById([this.user.role, ...fetchedRoles]);
      } else {
        this.rolesList = fetchedRoles;
      }
    } else {
      this.rolesList = fetchedRoles;
    }

    this.rolesTotalRecords = rolesPage.totalCount ?? 0;
  }

  private async updateValidatorsBasedOnRoles(role: any): Promise<void> {
    if (!role) {
      console.warn('No role provided for validator update.');
      return;
    }

    console.log('role', role);
    const roleRestrictions = await firstValueFrom(this.rolesService.apiRolesIdRestrictionsGet(role.id));
    console.log('roleRestrictions', roleRestrictions)

    const marketRestriction = roleRestrictions.restrictions?.find((restriction: any) =>
      restriction === Restrictions.MarketsRestriction
    );
    const retailerRestriction = roleRestrictions.restrictions?.find((restriction: any) =>
      restriction === Restrictions.RetailerRestriction
    );

    const hasMarketRestriction = !!marketRestriction;
    const hasRetailerRestriction = !!retailerRestriction;

    console.log('Evaluating role restrictions...');
    console.log('Role:', role?.name);

    console.log('Has Market Restriction:', hasMarketRestriction, '| Matched Entry:', marketRestriction);
    console.log('Has Retailer Restriction:', hasRetailerRestriction, '| Matched Entry:', retailerRestriction);

    this.isMarketsFieldDisabled = hasMarketRestriction;
    this.isRetailersFieldDisabled = hasRetailerRestriction;

    console.log('isMarketsFieldDisabled', this.isMarketsFieldDisabled);
    console.log('isRetailersFieldDisabled', this.isRetailersFieldDisabled);

    if (!hasMarketRestriction) {
      this.userForm.get('marketsIds')?.clearValidators();
    }

    if (!hasRetailerRestriction) {
      this.userForm.get('storesIds')?.clearValidators();
    }

    this.userForm.get('storesIds')?.updateValueAndValidity();
    this.userForm.get('marketsIds')?.updateValueAndValidity();
  }

  onLazyLoadStores(event: any) {
    this.getStores(event);
  }

  async getStores(event: any) {
    const filters: any[] = [];
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, event?.pageIndex ?? 1);
    const payload : FilterRequestForCombo = {
      pageNumber: activePage,
      pageSize: event?.pageSize ?? this.storesRowsPerPageOption
    }
    if (search) {
      payload.textFilter = search;
    }

    filters.push({
      propertyName: 'isActive',
      operator: this.filterOperator.EQUALS,
      values: ['true']
    });
    payload.additionalFilters = filters;

    const storesPage = await firstValueFrom(this.retailersService.apiRetailersFilterComboPost(payload));

    const assignedRetailers = Array.isArray(this.user?.retailers) ? this.user.retailers : [];

    const assignedRetailerOptions = assignedRetailers.map((retailer: any) => ({
      id: retailer.id,
      text: retailer.name
    }));

    const allVisibleStores = this.removeDuplicatesById([
      ...assignedRetailerOptions,
      ...(storesPage.results ?? [])
    ]);

    this.storesList = allVisibleStores;
    this.storesTotalRecords = storesPage.totalCount ?? 0;
  }

  onLazyLoadMarkets(event: any) {
    this.getMarkets(event);
  }

  async getMarkets(event: any) {
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, event?.pageIndex ?? 1);
    const payload : FilterRequestForCombo = {
      pageNumber: activePage,
      pageSize: event?.pageSize ?? this.marketsRowsPerPageOption
    }
    if (search) {
      payload.textFilter = search;
    }

    const marketsPage = await firstValueFrom(this.marketsService.apiMarketsFilterComboPost(payload));

    const assignedMarkets = Array.isArray(this.user?.markets) ? this.user.markets : [];

    const assignedMarketOptions = assignedMarkets.map((market: any) => ({
      id: market.id,
      text: market.name
    }));

    const allVisibleMarkets = this.removeDuplicatesById([
      ...assignedMarketOptions,
      ...(marketsPage.results ?? [])
    ]);

    this.marketsList = allVisibleMarkets;
    this.marketsTotalRecords = marketsPage.totalCount ?? 0;
  }

  private removeDuplicatesById(items: any[]): any[] {
    const uniqueItems: any[] = [];
    const seenIds = new Set();

    for (const item of items) {
      if (!seenIds.has(item?.id)) {
        uniqueItems.push(item);
        seenIds.add(item?.id);
      }
    }

    return uniqueItems;
  }

  isRoleSelected(): boolean {
    const value = this.userForm.get('roles')?.value;
    return Array.isArray(value) && value.length > 0;
  }

  isSubmitDisabled(): boolean {
    const formInvalid = this.userForm.invalid;
    return formInvalid;
  }

  async submitForm() {
    if (this.isSubmitDisabled()) {
      return;
    }

    const userId = this.userId;

    const selectedLanguageCode = this.userForm.controls['selectedLanguage'].value?.[0] ?? null;
    const selectedLanguage: any = this.languages.find((lang: any) => lang.code === selectedLanguageCode);

    const selectedRoleId = this.userForm.controls['roles'].value?.[0] ?? null;

    const retailerIds: number[] = (this.userForm.controls['stores'].value || [])
      .map((id: any) => Number(id))
      .filter((id: number) => !Number.isNaN(id));

    const marketIds: number[] = (this.userForm.controls['markets'].value || [])
      .map((id: any) => Number(id))
      .filter((id: number) => !Number.isNaN(id));


    const payload: UserRequest = {
      firstName: this.userForm.controls['firstname'].value,
      lastName: this.userForm.controls['lastname'].value,
      romanisedFirstName: this.userForm.controls['romanisedFirstName'].value,
      romanisedLastName: this.userForm.controls['romanisedLastName'].value,
      languageId: selectedLanguage?.id ?? null,
      username: this.userForm.controls['username'].value,
      email: this.userForm.controls['email'].value,
      roleId: selectedRoleId ?? undefined,
      marketIds: marketIds,
      retailerIds: retailerIds,
      allowedChangePassword: this.userForm.controls['allowPasswordChange'].value === 'true',
    };

    await firstValueFrom(this.usersService.apiUsersUpdateIdPut(userId, payload));

    this.router.navigate(['/users', this.userId]);
  }

}
